import { Editor, type EditorEvent, type Events, type Ui } from 'tinymce';
import { NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import * as FlatModels from '@medsurf/flat-models';
import { MediaControlService } from '@medsurf/flat-services';
import * as BasePlugin from '../base/media.base.plugin.editor';

export const register = (
  editor: Editor,
  zone: NgZone,
  translate: TranslateService,
  dialogService: DialogService,
  mediaControlService: MediaControlService
) => {
  const nodeChangedCallback = (
		buttonApi: Ui.Toolbar.ToolbarToggleButtonInstanceApi, 
		event: EditorEvent<Events.NodeChangeEvent>
	) => {
    const element = (<HTMLElement>event.element);
    const active = element?.tagName?.toLowerCase() === 'a' && !!element?.dataset['id'] && !element.classList.contains('mds-external') && !element.classList.contains('mds-internal');
    const hasSelectionContent = editor.selection.getContent().length > 0;

    const enable = active && !hasSelectionContent || hasSelectionContent && !/<(\bvideo\b|\baudio\b|\bimg\b|\ba\b)[\s\S]/i.test(editor.selection.getContent());

    buttonApi.setEnabled(enable || !hasSelectionContent);
    buttonApi.setActive(enable && active);
  }

  const onSetup = (buttonApi: Ui.Toolbar.ToolbarToggleButtonInstanceApi) => {
    editor.on('NodeChange', (event) => nodeChangedCallback(buttonApi, event));
    return () => {
      editor.off('NodeChange', (event) => nodeChangedCallback(buttonApi, event));
    };
  };

  const onAction = () => {
    const selection = editor.selection.getContent();
    const allowedTypes: FlatModels.MediaEntityModels.MediaType[] = [FlatModels.MediaEntityModels.MediaType.enum.MediaDocument];

    const node = <HTMLElement>editor.selection.getNode();
    const isAnchor = node?.tagName?.toLowerCase() === 'a';
    const id = node.dataset['id'];

    const selected: FlatModels.ModalAuthorModels.SelectedMediaDialogItem | undefined =
      isAnchor && id ? { id, type: FlatModels.MediaEntityModels.MediaType.enum.MediaDocument } : undefined;

    try {
      zone.run(async () => {

        editor.editorManager.focusedEditor = null;
        const media = await BasePlugin.openMediaDialog(
          translate.instant("global.pdf_edit"),
          dialogService,
          allowedTypes,
          selected
        )
        editor.editorManager.focusedEditor = editor;

        if (!media) return;
        const url = mediaControlService.getEditorUrl(media);
            
        if (isAnchor) {
          node.className = '';
          node.innerText = node.innerText.match(/.+?\.pdf$/) ? media.name : node.innerText;
          editor.dom.setAttribs(node, {
            href: url,
            'data-id': media.id
          });
          editor.dispatch('change');
        } else {
          const anchor = document.createElement('a');
          anchor.setAttribute('href', url);
          anchor.setAttribute('data-id', media.id);
          anchor.setAttribute('target', '_blank');
          anchor.innerHTML = selection || media.name;
          editor.insertContent(anchor.outerHTML);
        }
      });
    } catch (err) {
      return;
    }
  }

  editor.ui.registry.addToggleButton('mediapdf', {
    icon: 'pdf',
    tooltip: 'Insert/edit pdf',
    onAction,
    onSetup
  });
}